<script setup>
import get from 'lodash/get';
import { ref, computed, nextTick } from 'vue';

const props = defineProps(['fileUrl', 'label']);

const emit = defineEmits(['file-uploaded', 'delete-file']);

const file = ref(null);
const fileUploaderElement = ref(null);
const showFile = ref(!!props.fileUrl);
const showRemoveButton = ref(false);
const shouldDeleteFile = ref(false);
// const errorLoading = ref(false);

const showFileImage = computed(() => {
  if (!showFile.value) {
    return false;
  }
  return showFile.value || !get(file.value, 'fileList[0]', null);
});

const removeFile = async () => {
  await nextTick();
  if (get(file.value, 'files.length')) {
    // todo
    return fileUploaderElement.value?.$el.querySelector('.formulate-file-remove').click?.();
  }
  if (showFile.value) {
    // Avatar exists already
    shouldDeleteFile.value = true;
    emit('delete-file');
  }
  showFile.value = false;
  showRemoveButton.value = false;
};

const uploadFile = (file, progress) => {
  // Vue Formulate's file component is highly opinionated, we use it totally different.
  // Here we are just settings the progress to 100 manually to override the default unwanted delay
  progress(100);
};
//
</script>

<template>
  <div>
    <div v-show="showFileImage" class="img-container">
      <div class="overlay"></div>
      <label class="file-label" for="logo">{{ label }}</label>
      <div class="control-icons">
        <div
          @click="
            fileUploaderElement?.$el.querySelector('input').click();
            shouldDeleteFile = false;
          "
          class="control tooltip-top tooltip-light"
          data-tooltip="Edit"
        >
          <app-icon name="edit-pen" type="svg" class="icon" />
        </div>
        <div @click="removeFile" class="control tooltip-top tooltip-light" data-tooltip="Remove">
          <ion-icon name="trash-outline" class="icon" />
        </div>
      </div>
      <img @error="showFile = false" :src="fileUrl" width="124" height="124" alt="Logo" class="img" />
    </div>
    <formulate-input
      v-show="!showFileImage"
      v-model="file"
      @change="showFile = false"
      @file-upload-complete="
        emit('file-uploaded', get(file, 'fileList[0]'));
        showRemoveButton = $event.complete;
      "
      :uploader="uploadFile"
      type="image"
      ref="fileUploaderElement"
      name="_file"
      :label="label"
      :class="{ 'has-file': get(file, 'fileList[0]') }"
      validation="mime:image/jpeg,image/png,image/gif"
    >
      <template #help>
        <div class="control-icons">
          <div
            @click="
              fileUploaderElement?.$el.querySelector('input').click();
              shouldDeleteFile = false;
            "
            class="cursor-pointer control tooltip-top tooltip-light"
            data-tooltip="Edit"
          >
            <app-icon name="edit-pen" type="svg" class="icon" />
          </div>
          <div v-show="showRemoveButton" @click="removeFile" class="cursor-pointer control tooltip-top tooltip-light" data-tooltip="Remove">
            <ion-icon name="trash-outline" class="icon" />
          </div>
        </div>
        <div v-if="!get(file, 'fileList[0]', null)" class="file-help">Min 400 x 400 px</div>
      </template>
    </formulate-input>
  </div>
</template>

<style lang="scss">
.control-icons {
  z-index: 100;
  display: none;
  position: absolute;
  width: 100%;
  top: calc(50% + 9px);
  transform: translateY(-50%);
  justify-content: center;

  .control {
    margin: 2px;
    padding: 10px;
    display: flex;
    align-items: center;
    border-radius: 50%;
    background-color: var(--gray-light-5);
  }
}

[data-type='image']:hover {
  &.has-file {
    .formulate-input-upload-area {
      background: #00000069;
      border-radius: 8px;
    }

    .control-icons {
      display: flex;
    }
  }
}

.overlay {
  display: none;
  background: #00000069;
  position: absolute;
  top: 24px;
  right: 0;
  width: 123px;
  height: 123px;
  border-radius: 8px;
  border: 1px solid transparent;
}

.img-container {
  position: relative;
  display: inline-block;
  cursor: pointer;
  transition: 0.2s ease-out;

  .file-label {
    margin: 4px 0;
    display: block;
    font-size: 13px;
    font-weight: 600;
    line-height: 16px;
  }

  .img {
    border: 1px solid #ececf1;
    border-radius: 8px;
    object-fit: contain;
    width: 124px;
    height: 124px;
  }

  &:hover {
    .control-icons {
      display: flex;
    }

    .overlay {
      display: block;
    }
  }
}
</style>
